import React, { useState, useRef, useEffect } from 'react';
import './SideBar.css';
import OnlineStatusIndicator from '../onlinestatus/OnlineStatusIndicator';
import { API_BASE } from '../../config/api';

export default function SideBar({ setSelectedFriend, onlineUsers }) {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [friends, setFriends] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [sentRequests, setSentRequests] = useState([]);
  const [isOpen, setIsOpen] = useState(false);

  const notificationRef = useRef(null);
  const searchRef = useRef(null);
  const searchDropdownRef = useRef(null);

  useEffect(() => {
    fetchInitialData();

    // set up polling every 10 seconds
    const interval = setInterval(() => {
      fetchFriends();
    }, 10000);

    return () => clearInterval(interval);
  }, []);

  const fetchInitialData = () => {
    fetchRequests();
    fetchFriends();
  }
  const fetchRequests = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/friends/requests`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (res.ok) {
        const data = await res.json();
        setPendingRequests(data.requests || []);
      }
    } catch (err) {
      setPendingRequests([]);
    }
  };

  const fetchFriends = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/friends/all`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (res.ok) {
        const data = await res.json();
        setFriends(data.friends || []);
      }
    } catch (err) {
      setFriends([]);
    }
  };

  const handleSearch = async (e) => {
    const value = e.target.value;
    setSearch(value);

    if (value.trim().length === 0) {
      setResults([]);
      return;
    }

    try {
      const res = await fetch(`${API_BASE}/api/auth/search?username=${value}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (res.ok) {
        const data = await res.json();
        setResults(data.users || []);
      } else {
        setResults([]);
      }
    } catch (err) {
      setResults([]);
    }
  };

  const sendFriendRequest = async (toUserId) => {
    try {
      const res = await fetch(`${API_BASE}/api/friends/request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ toUserId })
      });
      const data = await res.json();
      if (res.ok) {
        alert('Friend request sent!');
        setSentRequests(prev => [...prev, toUserId]);
      } else {
        alert(data.message || 'Failed to send request');
      }
    } catch (err) {
      alert('Error sending request');
    }
  };

  const acceptRequest = async (fromUserId) => {
    try {
      const res = await fetch(`${API_BASE}/api/friends/accept`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ fromUserId })
      });
      const data = await res.json();
      if (res.ok) {
        alert('Friend added!');
        setPendingRequests(prev => prev.filter(req => req.id !== fromUserId));
        setFriends(prev => [...prev, data.friend]);
      } else {
        alert(data.message || 'Failed to accept request');
      }
    } catch (err) {
      alert('Server error');
    }
  };


  const declineRequest = (id) => {
    alert(`Declined request from user id: ${id}`);
  };

  const toggleNotifications = () => {
    setShowNotifications(prev => !prev);
    setResults([]); // close search dropdown if opening notifications
  };

  return (
    <>
      {/* 👇 Hamburger button (visible only on mobile) */}
      <button className="hamburger" onClick={() => setIsOpen(!isOpen)}>
        ☰
      </button>

      <div className={`sidebar ${isOpen ? 'open' : ''}`}>
        {/* your existing sidebar code */}
        <div className="searchbar-container" style={{ position: "relative" }}>
          <input
            type="text"
            className="searchbar"
            placeholder="Search by Username"
            value={search}
            onChange={handleSearch}
            ref={searchRef}
            onFocus={() => setShowNotifications(false)}
          />

          {results.length > 0 && (
            <div className="search-dropdown" ref={searchDropdownRef}>
              {results.map((user) => {
                const isFriend = friends.some(f => f.id === user._id || f._id === user._id);
                const isRequestPending = sentRequests.includes(user._id);

                return (
                  <div key={user._id} className="dropdown-item">
                    <span className="username">{user.username}</span>
                    {isFriend ? (
                      <button className="friend-btn" disabled>Friend</button>
                    ) : isRequestPending ? (
                      <button className="pending-btn" disabled>Pending</button>
                    ) : (
                      <button
                        className="add-btn"
                        onClick={() => sendFriendRequest(user._id)}
                      >
                        Add
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <div
            className="notification-wrapper"
            title="Notifications"
            onClick={toggleNotifications}
            style={{ cursor: 'pointer' }}
            ref={notificationRef}
          >
            <img
              src="/images/notification.png"
              alt="Notifications"
              className="notification-icon"
            />
            {pendingRequests.length > 0 && (
              <span className="notification-badge">{pendingRequests.length}</span>
            )}
            {showNotifications && (
              <div className="notifications-dropdown">
                {pendingRequests.length === 0 ? (
                  <div className="no-requests">No pending friend requests</div>
                ) : (
                  pendingRequests.map((req) => (
                    <div key={req.id} className="notification-item">
                      <span className="username">{req.username}</span>
                      <div className="actions">
                        <button className="accept-btn" onClick={() => acceptRequest(req.id)}>Accept</button>
                        <button className="decline-btn" onClick={() => declineRequest(req.id)}>Decline</button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
        <div className="friends-list">
          <h4>Friends</h4>
          {friends.map((friend) => {
            const friendId = String(friend.id || friend._id);
            const isOnline = onlineUsers.includes(friendId);

            return (
              <div
                key={friendId}
                onClick={() => setSelectedFriend(friend)}
                className="friend-item"
                style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', marginBottom: '8px' }}
              >
                <OnlineStatusIndicator isOnline={isOnline} />
                {friend.username}
              </div>
            );
          })}
        </div>

      </div>
    </>
  );
}